import React, { Component, ErrorInfo, ReactNode } from "react";
import {
  captureClientError,
  maybeReloadOnChunkError,
} from "../utils/clientEvents";

type Props = {
  children: ReactNode;
  /**
   * Shown instead of `children` after a render error. Omit (`undefined`) for
   * the default "Something went wrong" panel; pass `null` to render nothing.
   */
  fallback?: ReactNode;
};

type State = {
  hasError: boolean;
};

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    captureClientError(error, {
      componentStack: info.componentStack?.slice(0, 4000),
      source: "react_error_boundary",
    });
    // A lazy-route chunk that failed to load after a deploy is thrown during
    // render and caught here, never reaching window.onerror. Route it through
    // the shared chunk-error handler so the stale tab reloads onto the new
    // shell instead of getting stuck on the fallback screen below. (No-op for
    // non-chunk render errors.)
    maybeReloadOnChunkError(error, error?.message ?? "");
  }

  render() {
    if (this.state.hasError) {
      // `undefined` = not provided → default panel; an explicit `null` means
      // "render nothing on failure" and must not fall through to the panel.
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <h2>Something went wrong.</h2>
          <p>
            We&apos;ve been notified. Please refresh the page to try again.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}
